import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { BrokerListRow } from "../../../types/brokers";

type Busy = null | "enable" | "disable";

export default function BrokerRowActions({
  row,
  token,
  confirm,
  onReload,
}: {
  row: BrokerListRow;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
  onReload: () => void;
}) {
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);

  const handleEnable = async () => {
    const ok = await confirm({
      title: "Activar broker",
      message: `El broker "${row.brokerName}" pasará a estado Activo y podrá operar. ¿Continuar?`,
      confirmLabel: "Activar",
      tone: "primary",
    });
    if (!ok) return;
    setBusy("enable");
    setError(null);
    try {
      await request(
        `/api/admin/operations/brokers/${row.brokerInternalId}/enable`,
        { method: "PUT", token },
      );
      onReload();
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo activar");
    } finally {
      setBusy(null);
    }
  };

  const handleDisable = async () => {
    const ok = await confirm({
      title: "Deshabilitar broker",
      message: `El broker "${row.brokerName}" pasará a estado Deshabilitado. Los empleados existentes no podrán operar. ¿Continuar?`,
      confirmLabel: "Deshabilitar",
      tone: "danger",
    });
    if (!ok) return;
    setBusy("disable");
    setError(null);
    try {
      await request(
        `/api/admin/operations/brokers/${row.brokerInternalId}/disable`,
        { method: "PUT", token },
      );
      onReload();
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo deshabilitar");
    } finally {
      setBusy(null);
    }
  };

  const canEnable = row.status !== "active";
  const canDisable = row.status !== "disabled";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
        alignItems: "stretch",
        minWidth: 130,
      }}
    >
      {canEnable && (
        <ActionButton
          onClick={handleEnable}
          tone="primary"
          loading={busy === "enable"}
          disabled={!!busy}
        >
          Activar
        </ActionButton>
      )}
      {canDisable && (
        <ActionButton
          onClick={handleDisable}
          tone="danger"
          loading={busy === "disable"}
          disabled={!!busy}
        >
          Deshabilitar
        </ActionButton>
      )}
      {error && (
        <span style={{ color: "#b32424", fontSize: ".7rem" }}>{error}</span>
      )}
    </div>
  );
}

function ActionButton({
  onClick,
  tone,
  loading,
  disabled,
  children,
}: {
  onClick: () => void;
  tone: "primary" | "danger" | "neutral";
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const palette =
    tone === "danger"
      ? { bg: "#fde2e2", fg: "#b32424", border: "#f5c6cb" }
      : tone === "primary"
        ? { bg: "#0069d9", fg: "white", border: "#0069d9" }
        : { bg: "white", fg: "#1f2933", border: "#dee2e6" };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "0.3rem 0.6rem",
        borderRadius: "0.35rem",
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontSize: ".74rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.6 : 1,
      }}
    >
      {loading ? "…" : children}
    </button>
  );
}
